import { create } from 'zustand'

const COMPLETE_UPLOAD_PERCENTAGE = 100

type UploadProgress = {
  percentage: number
  phase: 'uploading'
}

type UploadState = 'failed' | 'idle' | 'ready' | 'uploading'

export type PendingUploadItemInput = {
  file: File
  fileId?: string
  uploadUrl: string
}

export type PendingUploadItem = PendingUploadItemInput & {
  previewUrl: string
}

interface PendingUploadStore {
  conversationId: string | undefined
  pendingUploads: PendingUploadItem[]
  uploadError: string | undefined
  uploadProgress: UploadProgress | undefined
  uploadState: UploadState
  failUpload: (message: string) => void
  finishUpload: () => void
  resetUploadState: () => void
  setUploadProgress: (progress: UploadProgress) => void
  setPendingUploads: (conversationId: string, items: PendingUploadItemInput[]) => void
  /** @deprecated Prefer setPendingUploads for multi-file support */
  setPendingUpload: (file: File, conversationId: string, uploadUrl: string, fileId?: string) => void
  setConversationId: (id: string | undefined) => void
  startUpload: () => void
  clearPendingUpload: () => void
  clearVideo: () => void
}

const revokePreviewUrl = (previewUrl: string | undefined) => {
  if (previewUrl) {
    URL.revokeObjectURL(previewUrl)
  }
}

const revokePreviewUrls = (items: PendingUploadItem[]) => {
  for (const item of items) {
    revokePreviewUrl(item.previewUrl)
  }
}

const toPendingUploadItems = (items: PendingUploadItemInput[]): PendingUploadItem[] => {
  return items.map(item => ({
    ...item,
    previewUrl: URL.createObjectURL(item.file),
  }))
}

export const usePendingUploadStore = create<PendingUploadStore>((set, get) => ({
  conversationId: undefined,
  pendingUploads: [],
  uploadError: undefined,
  uploadProgress: undefined,
  uploadState: 'idle',
  setPendingUploads: (conversationId: string, items: PendingUploadItemInput[]) => {
    revokePreviewUrls(get().pendingUploads)
    set({
      pendingUploads: toPendingUploadItems(items),
      conversationId,
      uploadError: undefined,
      uploadProgress: undefined,
      uploadState: 'idle',
    })
  },
  setPendingUpload: (file: File, conversationId: string, uploadUrl: string, fileId?: string) => {
    get().setPendingUploads(conversationId, [{ file, fileId, uploadUrl }])
  },
  setConversationId: (id: string | undefined) => {
    set({ conversationId: id })
  },
  startUpload: () => {
    set({
      uploadError: undefined,
      uploadProgress: { percentage: 0, phase: 'uploading' },
      uploadState: 'uploading',
    })
  },
  setUploadProgress: (progress: UploadProgress) => {
    set({
      uploadProgress: progress,
      uploadState: progress.phase,
    })
  },
  failUpload: (message: string) => {
    set({
      uploadError: message,
      uploadProgress: undefined,
      uploadState: 'failed',
    })
  },
  finishUpload: () => {
    set({
      pendingUploads: get().pendingUploads.map(item => ({
        ...item,
        uploadUrl: '',
      })),
      uploadError: undefined,
      uploadProgress: { percentage: COMPLETE_UPLOAD_PERCENTAGE, phase: 'uploading' },
      uploadState: 'ready',
    })
  },
  resetUploadState: () => {
    set({
      uploadError: undefined,
      uploadProgress: undefined,
      uploadState: 'idle',
    })
  },
  clearPendingUpload: () => {
    revokePreviewUrls(get().pendingUploads)
    set({
      pendingUploads: [],
      uploadError: undefined,
      uploadProgress: undefined,
      uploadState: 'idle',
    })
  },
  clearVideo: () => {
    revokePreviewUrls(get().pendingUploads)
    set({
      conversationId: undefined,
      pendingUploads: [],
      uploadError: undefined,
      uploadProgress: undefined,
      uploadState: 'idle',
    })
  },
}))

export const selectPendingUploadPreviewUrls = (state: PendingUploadStore): string[] => {
  return state.pendingUploads.map(item => item.previewUrl).filter(Boolean)
}

export const selectPendingUploadFile = (state: PendingUploadStore): File | undefined => {
  return state.pendingUploads[0]?.file
}

export const selectPendingUploadUrl = (state: PendingUploadStore): string | undefined => {
  return state.pendingUploads[0]?.uploadUrl || undefined
}

export const selectPendingUploadPreviewUrl = (state: PendingUploadStore): string | undefined => {
  return state.pendingUploads[0]?.previewUrl
}
